import { Link, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { PenLine, Sparkles, FileText, Share2, ArrowRight, Check } from "lucide-react";
import { setAppLanguage, SUPPORTED_LANGUAGES } from "@/i18n";

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const current = (i18n.language || "en").split("-")[0];
  useEffect(() => { if (user) navigate("/app/log"); }, [user, navigate]);

  const steps = [
    { icon: PenLine, t: t("landing.stepLogT"), d: t("landing.stepLogD") },
    { icon: FileText, t: t("landing.stepContextT"), d: t("landing.stepContextD") },
    { icon: Sparkles, t: t("landing.stepGenerateT"), d: t("landing.stepGenerateD") },
    { icon: Share2, t: t("landing.stepShareT"), d: t("landing.stepShareD") },
  ];

  return (
    <div className="min-h-screen bg-background text-navy">
      <header className="flex items-center justify-between px-6 md:px-12 py-5 max-w-7xl mx-auto">
        <div className="flex items-center gap-2">
          <img src="/jotbay-logo.png" alt="JotBay" className="h-9 w-auto" data-testid="landing-logo" />
        </div>
        <div className="flex items-center gap-3">
          <div className="inline-flex rounded-full bg-slate-100 p-0.5 mr-1" data-testid="landing-language-switcher">
            {SUPPORTED_LANGUAGES.map((l) => (
              <button key={l.code} type="button" onClick={() => setAppLanguage(l.code)}
                data-testid={`landing-lang-${l.code}`}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold uppercase transition-colors ${
                  current === l.code ? "bg-white text-navy shadow-sm" : "text-slatebody hover:text-navy"
                }`} title={l.native}>{l.code}</button>
            ))}
          </div>
          <Link to="/login"><Button variant="ghost" data-testid="landing-login-btn">{t("landing.login")}</Button></Link>
          <Link to="/signup"><Button className="bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0]" data-testid="landing-signup-btn">{t("landing.startFree")}</Button></Link>
        </div>
      </header>

      <section className="max-w-5xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-16 text-left">
        <div className="inline-flex items-center gap-2 rounded-full bg-accent px-3 py-1 text-xs font-mono text-accent-foreground mb-6 animate-fade-up">
          <span className="h-1.5 w-1.5 rounded-full bg-[#ECECEC]" /> {t("landing.trialBadge")}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-3xl leading-[1.05] animate-fade-up">
          {t("landing.heroLine1")}<br />
          <span className="text-[#202123]">{t("landing.heroLine2a")}</span>
        </h1>
        <p className="mt-6 text-lg text-slatebody max-w-2xl leading-relaxed animate-fade-up">
          {t("landing.heroDesc")}
        </p>
        <div className="mt-8 flex flex-wrap gap-3 animate-fade-up">
          <Link to="/signup">
            <Button size="lg" className="bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0] gap-2" data-testid="hero-cta-btn">
              {t("landing.getStarted")} <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 md:px-12 pb-24">
        <div className="grid md:grid-cols-4 gap-4">
          {steps.map((f, i) => (
            <div key={i} className="rounded-lg border border-slate-200 bg-white p-6 hover:-translate-y-[2px] transition-transform duration-200">
              <div className="h-10 w-10 rounded-md bg-accent flex items-center justify-center mb-4">
                <f.icon className="h-5 w-5 text-[#202123]" />
              </div>
              <p className="font-mono text-xs text-slatebody mb-1">{t("landing.step", { n: i + 1 })}</p>
              <h3 className="font-display font-semibold text-lg">{f.t}</h3>
              <p className="text-sm text-slatebody mt-1.5 leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>

        <div className="mt-14 rounded-xl border border-slate-200 bg-white p-8 md:p-10 max-w-3xl">
          <p className="font-mono text-xs text-[#202123] mb-3">{t("landing.withContext")}</p>
          <p className="text-slatebody text-sm mb-1">{t("landing.rawLogLabel")} <em>{t("landing.rawLog")}</em></p>
          <p className="text-navy leading-relaxed mt-3">{t("landing.exampleOutput")}</p>
          <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slatebody">
            {[t("landing.feat1"), t("landing.feat2"), t("landing.feat3")].map((x) => (
              <span key={x} className="inline-flex items-center gap-1.5"><Check className="h-4 w-4 text-[#202123]" /> {x}</span>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 mt-8" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/jotbay-logo.png" alt="JotBay" className="h-7 w-auto" />
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm text-slatebody">
            <Link to={`${current === "fr" ? "/fr" : ""}/terms`} className="hover:text-navy transition-colors" data-testid="footer-terms">{t("legal.linkTerms")}</Link>
            <span aria-hidden="true">·</span>
            <Link to={`${current === "fr" ? "/fr" : ""}/privacy`} className="hover:text-navy transition-colors" data-testid="footer-privacy">{t("legal.linkPrivacy")}</Link>
            <span aria-hidden="true">·</span>
            <Link to={`${current === "fr" ? "/fr" : ""}/ai-use`} className="hover:text-navy transition-colors" data-testid="footer-aiuse">{t("legal.linkAiUse")}</Link>
            <span aria-hidden="true">·</span>
            <Link to={`${current === "fr" ? "/fr" : ""}/cookies`} className="hover:text-navy transition-colors" data-testid="footer-cookies">{t("legal.linkCookies")}</Link>
          </div>
          <p className="text-xs text-slatebody" data-testid="footer-copyright">© 2026 Global ScienCentre Corporation</p>
        </div>
      </footer>
    </div>
  );
}
